import { useRef, useState } from 'react'
import { useRouter } from 'next/router'
import queryString from 'query-string'

import { useAppSelector } from 'redux/hooks'

import { DeckSearchTypeV3, generateDefaultDeckSearchV3 } from 'types/search'

import PhatButton from 'components/formElements/PhatButton'
import GlobalOverlayStack from 'components/elements/Overlay/GlobalOverlayStack'
import PhatDropdown from 'components/formElements/PhatDropdown'
import ColorToggles from 'components/cardSearchPanel/searchFormElements/colorToggles'
import FormatDropdown from 'components/cardSearchPanel/searchFormElements/formatDropdown'
import BracketDropdown from 'components/misc/BracketDropdown'
import CardAutocomplete from 'components/formElements/CardAutocomplete'
import DeckTagSelector from 'components/formElements/DeckTagSelector'
import PhatInput from 'components/formElements/PhatInput'
import Link from 'components/elements/Link'

import styles from './folderFitersOverlay.module.scss'

const FolderFilterOverlay = () => {
  const router = useRouter()

  const [open, setOpen] = useState(false)

  const commanderRef = useRef<any>(null)
  const cardRef = useRef<any>(null)

  const ownerUsername = useAppSelector(state => state.folders.rootFolder?.owner?.username)

  // prettier-ignore
  const [deckSearchState, setDeckSearchState] = useState<DeckSearchTypeV3>(generateDefaultDeckSearchV3(router.query, router.pathname))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const tiddyParams: Record<string, any> = { ...deckSearchState }

    // Clean up params so they don't make it into the URL
    for (const key of Object.keys(tiddyParams)) {
      const currentValue = tiddyParams[key]

      if (currentValue == null || currentValue == '' || currentValue == undefined) {
        delete tiddyParams[key]
      }
    }

    // Don't need either of these since they're cached or baked into the URL already
    delete tiddyParams.orderBy
    delete tiddyParams.folderId

    // Note that if they're searching, we always want the page to be the first for the "new" results
    router.push(`/folders/${router.query.folderId}/?${queryString.stringify({ ...tiddyParams, page: 1 })}`)
  }

  const handleUpdateSearch = (updatedParts: Partial<DeckSearchTypeV3>) =>
    setDeckSearchState({ ...deckSearchState, ...updatedParts })

  return (
    <>
      <PhatButton icon="add" type="button" onClick={() => setOpen(true)}>
        Filters
      </PhatButton>

      <GlobalOverlayStack open={open} onClose={() => setOpen(false)} className={styles.content}>
        <h2>Filter this folder</h2>

        <p>
          Note: If you want to search for decks that are NOT in this folder, you want{' '}
          <Link href={`/search/decks/?ownerUsername=${ownerUsername}&fromFolderId=${router.query.folderId}`}>
            {' '}
            Advanced Deck Search
          </Link>
        </p>

        <form onSubmit={handleSubmit} className={styles.submitRow}>
          <PhatInput header="Deck name" placeholder="Example deck name" />
          <PhatButton icon="search" color="green" type="submit">
            Filter folder
          </PhatButton>
        </form>

        <div className={styles.colorOptions}>
          <PhatDropdown
            header="Color options"
            value={deckSearchState.colorsInclude ? 'includes' : 'exact'}
            options={[
              {
                id: 'exact',
                label: 'Exactly match colors',
                onClick: () => handleUpdateSearch({ colorsInclude: false }),
              },
              {
                id: 'includes',
                label: 'Include selected colors',
                onClick: () => handleUpdateSearch({ colorsInclude: true }),
              },
            ]}
          />

          <ColorToggles
            stringValue={deckSearchState.colors}
            onStringValueChange={colors => handleUpdateSearch({ colors })}
            className={styles.colorToggles}
          />
        </div>

        <FormatDropdown
          header="Deck format"
          disabled={deckSearchState.packages}
          value={deckSearchState.deckFormat}
          onChange={deckFormat => handleUpdateSearch({ deckFormat })}
          className={styles.formatPicker}
        />

        <BracketDropdown
          header="Commander Bracket"
          value={deckSearchState.edhBracket}
          onChange={edhBracket => handleUpdateSearch({ edhBracket })}
          className={styles.formatPicker}
        />

        <CardAutocomplete
          fetchCard
          ref={commanderRef}
          disabled={deckSearchState.packages}
          onFocus={() => commanderRef.current.select()}
          header="Commander"
          placeholder="Ezuri, Claw of Progress"
          value={deckSearchState.commanderName}
          onChange={(_, { value: commanderName }) => handleUpdateSearch({ commanderName })}
          onSelect={card => handleUpdateSearch({ commanderName: card.name })}
        />

        <CardAutocomplete
          fetchCard
          ref={cardRef}
          onFocus={() => cardRef.current.select()}
          header="Card"
          placeholder="Sol Ring"
          value={deckSearchState.cardName}
          onChange={(_, { value: cardName }) => handleUpdateSearch({ cardName })}
          onSelect={card => handleUpdateSearch({ cardName: card.name })}
        />

        <DeckTagSelector
          label="Deck tag"
          onChange={deckTagName => handleUpdateSearch({ deckTagName })}
          onSelect={(_, deckTagName) => handleUpdateSearch({ deckTagName })}
        />

        <div className={styles.deckSize}>
          <PhatDropdown
            value={deckSearchState.sizeLogic || 'EQ'}
            header="Deck size"
            options={[
              { id: 'EQ', label: 'Equals', onClick: () => handleUpdateSearch({ sizeLogic: 'EQ' }) },
              { id: 'GTE', label: 'Greater-than equals', onClick: () => handleUpdateSearch({ sizeLogic: 'GTE' }) },
              { id: 'LTE', label: 'Less-than equals', onClick: () => handleUpdateSearch({ sizeLogic: 'LTE' }) },
            ]}
          />

          <PhatInput
            type="number"
            value={deckSearchState.size}
            placeholder="100"
            onChange={size => handleUpdateSearch({ size })}
          />
        </div>
      </GlobalOverlayStack>
    </>
  )
}

export default FolderFilterOverlay
